"use client";

/**
 * @module StackingCard
 *
 * Selectable card component with collapsible content. Supports single selection groups.
 * Features animated expand/collapse and accessible keyboard navigation.
 *
 * @example
 * // Single selectable card
 * <StackingCard selectable selected={isSelected} onSelectedChange={setSelected}>
 *   <StackingCardHeader>
 *     <StackingCardTitle>Premium Plan</StackingCardTitle>
 *   </StackingCardHeader>
 *   <StackingCardContent collapsible>
 *     <StackingCardList>
 *       <StackingCardListItem>Unlimited downloads</StackingCardListItem>
 *       <StackingCardListItem>Priority support</StackingCardListItem>
 *     </StackingCardList>
 *   </StackingCardContent>
 * </StackingCard>
 *
 * @example
 * // Radio group of cards
 * <StackingCardGroup value={plan} onValueChange={setPlan}>
 *   <StackingCard value="basic" selectable>
 *     <StackingCardHeader>
 *       <StackingCardTitle>Basic</StackingCardTitle>
 *     </StackingCardHeader>
 *   </StackingCard>
 *   <StackingCard value="pro" selectable>
 *     <StackingCardHeader>
 *       <StackingCardTitle>Pro</StackingCardTitle>
 *     </StackingCardHeader>
 *   </StackingCard>
 * </StackingCardGroup>
 */
import type { LucideProps } from "lucide-react";
import { Check, ChevronDown } from "lucide-react";
import type { Variants } from "motion/react";
import { motion } from "motion/react";
import * as React from "react";
import { cn } from "../lib/utils";
import { Button } from "./button";
import { DummyCheckbox } from "./checkbox";
import { Separator } from "./separator";
import type { ListItemProps, ListProps } from "./typography/list";
import { List, ListItem } from "./typography/list";
import { Text } from "./typography/text";

// Motion transition configuration
const motionTransition = {
  duration: 0.4,
  type: "spring" as const,
  bounce: 0
};
const motionVariantsChevron: Variants = {
  collapsed: {
    rotate: 0,
    transition: motionTransition
  },
  expanded: {
    rotate: 180,
    transition: motionTransition
  }
};

// Hook to handle auto height animation for collapsible content
const useAutoHeight = () => {
  const [height, setHeight] = React.useState<number | "auto">("auto");
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!ref.current) {
      return;
    }
    const resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) {
        setHeight(entry.contentRect.height);
      }
    });
    resizeObserver.observe(ref.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  const variants: Variants = React.useMemo(() => ({
    collapsed: {
      height: 0,
      opacity: 0,
      transition: motionTransition
    },
    expanded: {
      height: height === "auto" ? "auto" : height,
      opacity: 1,
      transition: motionTransition
    }
  }), [height]);
  return {
    ref,
    variants
  };
};
interface StackingCardGroupContextValue {
  expandedValue: string | null;
  onExpandedChange?: (value: string | null) => void;
  onValueChange?: (value: string) => void;
  registerCardRef?: (value: string, element: HTMLElement | null) => void;
  selectedValue: string;
}
const StackingCardGroupContext = React.createContext<StackingCardGroupContextValue | null>(null);
const useStackingCardGroup = () => {
  const context = React.useContext(StackingCardGroupContext);
  return context;
};
interface StackingCardContextValue {
  contentId?: string;
  isCollapsed?: boolean;
  isSelected?: boolean;
  onSelectedChange?: (selected: boolean) => void;
  onToggleCollapse?: () => void;
  selectable?: boolean;
  setHasCollapsibleContent?: (hasCollapsible: boolean) => void;
}
const StackingCardContext = React.createContext<StackingCardContextValue>({});
const useStackingCard = () => {
  const context = React.useContext(StackingCardContext);
  return context;
};
interface StackingCardProps extends React.HTMLAttributes<HTMLDivElement> {
  "aria-describedby"?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  initiallyCollapsed?: boolean;
  onSelectedChange?: (selected: boolean) => void;
  selectable?: boolean;
  selected?: boolean;
  value?: string;
  variant?: "default" | "selected";
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Complex component with many state interactions
function StackingCard({
  className,
  children,
  selectable = false,
  selected = false,
  onSelectedChange,
  variant,
  onClick,
  value,
  onKeyDown,
  initiallyCollapsed = true,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledby,
  "aria-describedby": ariaDescribedby,
  ref,
  ...props
}: StackingCardProps & {
  ref?: React.Ref<HTMLDivElement>;
}) {
  const [isCollapsed, setIsCollapsed] = React.useState(true);
  const [hasCollapsibleContent, setHasCollapsibleContent] = React.useState(false);
  const [wasSelected, setWasSelected] = React.useState(false);
  const ignoreInitiallyCollapsedAfterRenderRef = React.useRef(false);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Generate unique ID for this card instance
  const cardId = React.useId();
  const contentId = `${cardId}-content`;
  const group = useStackingCardGroup();

  // Determine if we're in a group
  const isInGroup = group !== null;

  // Determine selection state
  const isSelected = isInGroup ? group.selectedValue === value : variant === "selected" || selected;

  // Auto-expand logic: if card becomes selected and has collapsible content, expand it
  // This is calculated during render, not in useEffect
  const justBecameSelected = isSelected && !wasSelected;
  if (justBecameSelected && hasCollapsibleContent) {
    if (isInGroup) {
      // In group mode, expand this card
      group?.onExpandedChange?.(value ?? null);
    } else {
      // In standalone mode, expand this card
      setIsCollapsed(false);
    }
  }

  // Update wasSelected for next render (this is safe during render)
  if (isSelected !== wasSelected) {
    setWasSelected(isSelected);
  }

  // Determine collapse state
  let shouldBeCollapsed = isCollapsed;
  if (initiallyCollapsed && !ignoreInitiallyCollapsedAfterRenderRef.current) {
    shouldBeCollapsed = true;
  } else if (isInGroup) {
    shouldBeCollapsed = group.expandedValue !== value;
  }
  const ignoreInitiallyCollapsedRef = React.useCallback(() => {
    if (initiallyCollapsed && !ignoreInitiallyCollapsedAfterRenderRef.current) {
      ignoreInitiallyCollapsedAfterRenderRef.current = true;
    }
  }, [initiallyCollapsed]);
  const handleSelect = React.useCallback(() => {
    if (selectable) {
      if (isInGroup && value) {
        group?.onValueChange?.(value);
      } else if (!isInGroup && onSelectedChange) {
        onSelectedChange(!selected);
      }
    }
  }, [selectable, isInGroup, value, onSelectedChange, group, selected]);
  const handleToggleCollapse = React.useCallback(() => {
    if (isInGroup) {
      // In group mode, toggle between this card and null (all collapsed)
      group?.onExpandedChange?.(group.expandedValue === value ? null : value ?? null);
    } else {
      setIsCollapsed(!isCollapsed);
    }
  }, [isInGroup, group, value, isCollapsed]);
  const handleKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    // Immediately mark that we're no longer in the initial state
    ignoreInitiallyCollapsedRef();
    if (selectable && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      handleSelect();
    }
    onKeyDown?.(e);
  }, [selectable, handleSelect, onKeyDown, ignoreInitiallyCollapsedRef]);
  const handleClick = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();

    // Immediately mark that we're no longer in the initial state
    ignoreInitiallyCollapsedRef();
    handleSelect();
    if (isSelected) {
      handleToggleCollapse();
    }
    onClick?.(e);
  }, [handleSelect, onClick, isSelected, handleToggleCollapse, ignoreInitiallyCollapsedRef]);
  const contextValue: StackingCardContextValue = React.useMemo(() => ({
    isSelected,
    isCollapsed: shouldBeCollapsed,
    selectable,
    onSelectedChange,
    onToggleCollapse: handleToggleCollapse,
    setHasCollapsibleContent,
    contentId
  }), [isSelected, shouldBeCollapsed, selectable, onSelectedChange, handleToggleCollapse, contentId]);

  // Generate accessible label if not provided
  const accessibleLabel = ariaLabel ?? (isInGroup ? `Option ${value}` : "Card");

  // Compute role separately to avoid nested ternary
  const computeRole = (): "radio" | "button" | undefined => {
    if (isInGroup) {
      return "radio";
    }
    if (selectable) {
      return "button";
    }
    return;
  };
  const cardRole = computeRole();

  // Combined ref callback that handles both forwarded ref and group registration
  const combinedRef = React.useCallback((element: HTMLDivElement | null) => {
    // Handle forwarded ref
    if (typeof ref === "function") {
      ref(element);
    } else if (ref) {
      ref.current = element;
    }

    // Register with group if needed
    if (isInGroup && value && group?.registerCardRef) {
      group.registerCardRef(value, element);
    }
  }, [ref, isInGroup, value, group]);
  React.useEffect(() => {
    if (initiallyCollapsed && !ignoreInitiallyCollapsedAfterRenderRef.current) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        ignoreInitiallyCollapsedAfterRenderRef.current = true;
      }, 1500); // delay to allow for rapid re-renders to settle
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [initiallyCollapsed]);
  return <StackingCardContext.Provider value={contextValue} data-sentry-element="StackingCardContext.Provider" data-sentry-component="StackingCard" data-sentry-source-file="stacking-card.tsx">
      {/* biome-ignore lint/a11y/noStaticElementInteractions: Role is dynamically assigned based on context */}
      {/* biome-ignore lint/a11y/noNoninteractiveElementInteractions: Interactive behavior with proper role */}
      {/* biome-ignore lint/a11y/useAriaPropsSupportedByRole: aria-checked is valid for radio role */}
      <div aria-checked={isInGroup ? isSelected : undefined} aria-describedby={ariaDescribedby} aria-expanded={hasCollapsibleContent ? !shouldBeCollapsed : undefined} aria-label={accessibleLabel} aria-labelledby={ariaLabelledby} aria-pressed={!isInGroup && selectable ? isSelected : undefined} className={cn("relative flex w-full min-w-64 max-w-96 flex-col overflow-hidden border border-solid", "rounded-2xl shadow-xs transition-colors", selectable && "cursor-pointer", isSelected ? "border-gray-950 dark:border-white" : "border-gray-150 dark:border-gray-800", "focus-visible:ring-[var(--focus-ring)] focus-visible:ring-offset-2", className)} key={value} onClick={handleClick} onKeyDown={handleKeyDown} ref={combinedRef} role={cardRole} style={props.style} tabIndex={selectable ? 0 : undefined} {...props}>
        {children}
      </div>
    </StackingCardContext.Provider>;
}
type StackingCardHeaderProps = React.HTMLAttributes<HTMLDivElement> & {
  ref?: React.Ref<HTMLDivElement>;
};
function StackingCardHeader({
  className,
  children,
  ref,
  ...props
}: StackingCardHeaderProps) {
  const {
    selectable,
    isSelected
  } = useStackingCard();
  return <div className={cn("flex items-center justify-between gap-2 px-4 pt-4 pb-1", isSelected && "bg-gray-100 dark:bg-gray-800", className)} ref={ref} {...props} data-sentry-component="StackingCardHeader" data-sentry-source-file="stacking-card.tsx">
      <div className="flex flex-1 items-center gap-2">
        {selectable && <DummyCheckbox checked={isSelected} tabIndex={-1} variant="radio" />}
        {children}
      </div>
    </div>;
}
type StackingCardTitleProps = React.HTMLAttributes<HTMLDivElement> & {
  ref?: React.Ref<HTMLDivElement>;
};
function StackingCardTitle({
  className,
  ref,
  ...props
}: StackingCardTitleProps) {
  return <Text className={cn("font-sans font-semibold text-gray-950 text-sm leading-6 dark:text-white", className)} ref={ref} {...props} data-sentry-element="Text" data-sentry-component="StackingCardTitle" data-sentry-source-file="stacking-card.tsx" />;
}
type StackingCardDescriptionProps = React.HTMLAttributes<HTMLDivElement> & {
  ref?: React.Ref<HTMLDivElement>;
};
function StackingCardDescription({
  className,
  ref,
  ...props
}: StackingCardDescriptionProps) {
  return <Text className={cn("text-gray-700 text-sm dark:text-gray-200", className)} ref={ref} {...props} data-sentry-element="Text" data-sentry-component="StackingCardDescription" data-sentry-source-file="stacking-card.tsx" />;
}
const MotionChevron = ({
  isCollapsed
}: {
  isCollapsed: boolean;
}) => <motion.div animate={isCollapsed ? "collapsed" : "expanded"} className="flex items-center justify-center" initial="collapsed" variants={motionVariantsChevron} data-sentry-element="motion.div" data-sentry-component="MotionChevron" data-sentry-source-file="stacking-card.tsx">
    <ChevronDown className="h-3 w-3" data-sentry-element="ChevronDown" data-sentry-source-file="stacking-card.tsx" />
  </motion.div>;
interface StackingCardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  collapsible?: boolean;
  description?: React.ReactNode;
  minimal?: boolean;
  ref?: React.Ref<HTMLDivElement>;
}
function StackingCardContent({
  className,
  children,
  collapsible = false,
  minimal = false,
  description,
  ref,
  ...props
}: StackingCardContentProps) {
  const {
    isCollapsed,
    isSelected,
    onToggleCollapse,
    setHasCollapsibleContent,
    contentId
  } = useStackingCard();
  const {
    ref: autoHeightRef,
    variants: autoHeightVariants
  } = useAutoHeight();

  // Register this component as having collapsible content
  React.useEffect(() => {
    if (collapsible) {
      setHasCollapsibleContent?.(true);
    }
    return () => {
      setHasCollapsibleContent?.(false);
    };
  }, [collapsible, setHasCollapsibleContent]);
  if (!collapsible) {
    return <div className={cn("flex flex-col gap-1.5 px-4 pt-0 pb-4", isSelected && "bg-gray-100 dark:bg-gray-800", className)} ref={ref} {...props}>
        {children}
      </div>;
  }
  return <>
      <div className={cn("flex items-center justify-between gap-2 px-4 pb-4", isSelected && "bg-gray-100 dark:bg-gray-800")}>
        {description ? <div className="flex-1">{description}</div> : <div aria-hidden className="flex-1" />}
        <Button aria-controls={contentId} aria-expanded={!isCollapsed} aria-label={isCollapsed ? "Show details" : "Hide details"} borderRadius="full" className={cn(isSelected && "bg-white dark:bg-gray-900", "h-3")} onClick={e => {
        e.stopPropagation();
        onToggleCollapse?.();
      }} onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") {
          e.stopPropagation();
        }
      }} size="xs" variant="light" data-sentry-element="Button" data-sentry-source-file="stacking-card.tsx">
          <span className={cn({
          "sr-only": minimal
        })}>Details</span>
          <MotionChevron isCollapsed={isCollapsed ?? false} data-sentry-element="MotionChevron" data-sentry-source-file="stacking-card.tsx" />
        </Button>
      </div>
      <motion.div animate={isCollapsed ? "collapsed" : "expanded"} aria-hidden={isCollapsed} className="overflow-hidden" id={contentId} initial="collapsed" variants={autoHeightVariants} data-sentry-element="motion.div" data-sentry-source-file="stacking-card.tsx">
        <div className="flex flex-col" ref={autoHeightRef}>
          <Separator className={cn("mt-0 mb-0", isSelected ? "bg-gray-950 dark:bg-white" : "border-gray-150 dark:border-gray-800")} data-sentry-element="Separator" data-sentry-source-file="stacking-card.tsx" />
          <div className={cn("flex flex-col gap-1.5", className)} ref={ref} {...props}>
            {children}
          </div>
        </div>
      </motion.div>
    </>;
}
function StackingCardList({
  className,
  children,
  ref,
  ...props
}: ListProps & {
  ref?: React.Ref<HTMLUListElement | HTMLOListElement>;
}) {
  return <List className={cn("list-none px-4 pt-1.5 pb-5", className)} ref={ref} {...props} data-sentry-element="List" data-sentry-component="StackingCardList" data-sentry-source-file="stacking-card.tsx">
      {children}
    </List>;
}
const StackingCardCheck = React.memo<LucideProps>(({
  className,
  ...props
}) => {
  const {
    selectable,
    isSelected
  } = useStackingCard();
  return <Check className={cn("-mt-0.5 size-[14px]", {
    "text-current": !selectable,
    "text-brand-primary": isSelected && selectable,
    "text-gray-950 dark:text-white": !isSelected && selectable
  }, className)} {...props} />;
});
StackingCardCheck.displayName = "StackingCardCheck";
function StackingCardListItem({
  className,
  children,
  ref,
  ...props
}: ListItemProps & {
  ref?: React.Ref<HTMLLIElement>;
}) {
  return <ListItem className={cn("flex font-sans text-gray-700 text-sm first:mt-0 dark:text-gray-200", className)} customBullet={<StackingCardCheck />} ref={ref} {...props} data-sentry-element="ListItem" data-sentry-component="StackingCardListItem" data-sentry-source-file="stacking-card.tsx">
      {children}
    </ListItem>;
}
interface StackingCardGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  "aria-label"?: string;
  "aria-labelledby"?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  ref?: React.Ref<HTMLDivElement>;
  value?: string;
}
function StackingCardGroup({
  className,
  children,
  value,
  onValueChange,
  defaultValue,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledby,
  ref,
  ...props
}: StackingCardGroupProps) {
  const [internalSelectedValue, setInternalSelectedValue] = React.useState(defaultValue ?? "");
  const [internalExpandedValue, setInternalExpandedValue] = React.useState<string | null>(defaultValue ?? null);

  // For controlled mode, we need to manage expansion state separately
  const [controlledExpandedValue, setControlledExpandedValue] = React.useState<string | null>(value ?? null);

  // Calculate effective values during render (controlled vs uncontrolled pattern)
  const effectiveSelectedValue = value === undefined ? internalSelectedValue : value;
  const effectiveExpandedValue = value === undefined ? internalExpandedValue : controlledExpandedValue;

  // Auto-expand selected card in controlled mode
  React.useEffect(() => {
    if (value !== undefined) {
      setControlledExpandedValue(value);
    }
  }, [value]);
  const handleValueChange = React.useCallback((newValue: string) => {
    // Only update internal state if uncontrolled
    if (value === undefined) {
      setInternalSelectedValue(newValue);
      setInternalExpandedValue(newValue);
    } else {
      // In controlled mode, auto-expand the newly selected card
      setControlledExpandedValue(newValue);
    }
    onValueChange?.(newValue);
  }, [value, onValueChange]);
  const handleExpandedChange = React.useCallback((newExpandedValue: string | null) => {
    if (value === undefined) {
      // Uncontrolled mode
      setInternalExpandedValue(newExpandedValue);
    } else {
      // Controlled mode
      setControlledExpandedValue(newExpandedValue);
    }
  }, [value]);

  // Extract card values from children for arrow key navigation
  const cardValues = React.useMemo(() => {
    const values: string[] = [];
    React.Children.forEach(children, child => {
      if (React.isValidElement(child)) {
        const childValue = (child.props as {
          value?: unknown;
        }).value;
        if (typeof childValue === "string") {
          values.push(childValue);
        }
      }
    });
    return values;
  }, [children]);

  // Store card refs for navigation
  const cardRefsRef = React.useRef<Map<string, HTMLElement>>(new Map());
  const registerCardRef = React.useCallback((cardValue: string, element: HTMLElement | null) => {
    if (element) {
      cardRefsRef.current.set(cardValue, element);
    } else {
      cardRefsRef.current.delete(cardValue);
    }
  }, []);
  const handleKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (cardValues.length === 0) {
      return;
    }
    const currentIndex = cardValues.indexOf(effectiveSelectedValue);
    let nextIndex = currentIndex;
    switch (e.key) {
      case "ArrowDown":
      case "ArrowRight":
        e.preventDefault();
        nextIndex = currentIndex < cardValues.length - 1 ? currentIndex + 1 : 0;
        break;
      case "ArrowUp":
      case "ArrowLeft":
        e.preventDefault();
        nextIndex = currentIndex > 0 ? currentIndex - 1 : cardValues.length - 1;
        break;
      case "Home":
        e.preventDefault();
        nextIndex = 0;
        break;
      case "End":
        e.preventDefault();
        nextIndex = cardValues.length - 1;
        break;
      default:
        return;
    }
    const nextValue = cardValues[nextIndex];
    if (nextValue && nextValue !== effectiveSelectedValue) {
      handleValueChange(nextValue);

      // Focus the newly selected card using registered ref
      setTimeout(() => {
        const targetCard = cardRefsRef.current.get(nextValue);
        targetCard?.focus();
      }, 0);
    }
  }, [cardValues, effectiveSelectedValue, handleValueChange]);
  const contextValue: StackingCardGroupContextValue = React.useMemo(() => ({
    selectedValue: effectiveSelectedValue,
    expandedValue: effectiveExpandedValue,
    onValueChange: handleValueChange,
    onExpandedChange: handleExpandedChange,
    registerCardRef
  }), [effectiveSelectedValue, effectiveExpandedValue, handleValueChange, handleExpandedChange, registerCardRef]);

  // Generate accessible label if not provided
  const accessibleLabel = ariaLabel ?? "Select an option";
  return <StackingCardGroupContext.Provider value={contextValue} data-sentry-element="StackingCardGroupContext.Provider" data-sentry-component="StackingCardGroup" data-sentry-source-file="stacking-card.tsx">
      <div aria-label={accessibleLabel} aria-labelledby={ariaLabelledby} className={cn("flex flex-col gap-4", className)} onKeyDown={handleKeyDown} ref={ref} role="radiogroup" tabIndex={-1} {...props}>
        {children}
      </div>
    </StackingCardGroupContext.Provider>;
}
export { StackingCard, StackingCardCheck, StackingCardContent, StackingCardDescription, StackingCardGroup, StackingCardHeader, StackingCardList, StackingCardListItem, StackingCardTitle };